import { employeeDisplayName } from "./employeePeriod";
import type {
  CoachDailyTip,
  CoachEventAssignment,
  DailyTip,
  Employee,
  EmployeeRole,
  KitchenTipout,
  SquareTipShareEntry,
  TipEvent,
} from "./types";

/**
 * One payable line in the combined tip report: an employee as paid through a
 * single tip stream. A bartender who also works the front desk earns from two
 * separate pools and therefore gets two rows.
 *
 * `tips` excludes event money so `tips + extraTips + eventTips === total` holds
 * for every role. Bartender `calculated_tips` already has the event pool baked
 * in, so the event share is subtracted back out of `tips` rather than added on.
 */
export interface ReportRow {
  key: string;
  employeeId: string;
  name: string;
  roles: EmployeeRole[];
  primaryRole: EmployeeRole;
  hours: number;
  tips: number;
  extraTips: number;
  eventTips: number;
  total: number;
}

export interface ReportInput {
  employees: Employee[];
  tips: DailyTip[];
  barEvents: TipEvent[];
  kitchenTipouts: KitchenTipout[];
  squareEntries: SquareTipShareEntry[];
  coachTips: CoachDailyTip[];
  coachAssignments: CoachEventAssignment[];
}

/** Roles paid out of the Square tip-share sheet, most specific first. */
const SQUARE_ROLE_PRIORITY: EmployeeRole[] = ["axe_coach", "front_desk", "kitchen"];

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Distribute each day's net event gratuity across that day's bartenders in
 * proportion to hours worked, matching how Bartender Entry pooled it.
 */
function bartenderEventShares(tips: DailyTip[], barEvents: TipEvent[]): Map<string, number> {
  const netByDate = new Map<string, number>();
  for (const ev of barEvents) {
    netByDate.set(ev.date, (netByDate.get(ev.date) ?? 0) + Number(ev.net_to_pool));
  }

  const tipsByDate = new Map<string, DailyTip[]>();
  for (const t of tips) {
    const arr = tipsByDate.get(t.date) ?? [];
    arr.push(t);
    tipsByDate.set(t.date, arr);
  }

  const shares = new Map<string, number>();
  for (const [date, net] of netByDate) {
    if (net <= 0) continue;
    const dayTips = tipsByDate.get(date) ?? [];
    const dayHours = dayTips.reduce((s, t) => s + Number(t.hours_worked), 0);
    if (dayHours <= 0) continue;
    for (const t of dayTips) {
      const share = net * (Number(t.hours_worked) / dayHours);
      shares.set(t.employee_id, (shares.get(t.employee_id) ?? 0) + share);
    }
  }
  return shares;
}

export function buildReportRows(input: ReportInput): ReportRow[] {
  const { employees, tips, barEvents, kitchenTipouts, squareEntries, coachTips, coachAssignments } = input;

  const byId = new Map(employees.map((e) => [e.id, e]));
  const byName = new Map(employees.map((e) => [e.name.trim().toLowerCase(), e]));
  const rows = new Map<string, ReportRow>();

  const nameFor = (employeeId: string, fallback?: string): string => {
    const emp = byId.get(employeeId);
    return emp ? employeeDisplayName(emp) : fallback ?? employeeId;
  };

  /** Roles an employee can be paid under outside the bartender pool. */
  const payoutRoles = (emp: Employee | undefined): EmployeeRole[] =>
    (emp?.roles ?? []).filter((r) => r !== "bartender");

  const blank = (
    key: string,
    employeeId: string,
    name: string,
    roles: EmployeeRole[],
    primaryRole: EmployeeRole,
  ): ReportRow => ({ key, employeeId, name, roles, primaryRole, hours: 0, tips: 0, extraTips: 0, eventTips: 0, total: 0 });

  // ── Bartenders: nightly pool out of tips_daily_tips ──
  const eventShares = bartenderEventShares(tips, barEvents);
  for (const t of tips) {
    const key = `${t.employee_id}|bartender`;
    const row = rows.get(key) ?? blank(key, t.employee_id, nameFor(t.employee_id), ["bartender"], "bartender");
    row.hours += Number(t.hours_worked);
    row.total += Number(t.calculated_tips) || 0;
    rows.set(key, row);
  }
  for (const [employeeId, share] of eventShares) {
    const row = rows.get(`${employeeId}|bartender`);
    if (row) row.eventTips = share;
  }
  for (const row of rows.values()) {
    if (row.primaryRole === "bartender") row.tips = Math.max(0, row.total - row.eventTips);
  }

  // ── Front desk / kitchen / axe coach: the Square tip-share sheet ──
  const squareKey = (employeeId: string) => `${employeeId}|square`;
  for (const entry of squareEntries) {
    const roles = payoutRoles(byId.get(entry.employee_id));
    const primaryRole = SQUARE_ROLE_PRIORITY.find((r) => roles.includes(r)) ?? "front_desk";
    const key = squareKey(entry.employee_id);
    const row =
      rows.get(key) ??
      blank(key, entry.employee_id, nameFor(entry.employee_id), roles.length > 0 ? roles : [primaryRole], primaryRole);
    row.hours += Number(entry.hours_worked);
    row.tips += Number(entry.calculated_share);
    row.extraTips += Number(entry.extra_tips);
    row.eventTips += (entry.event_tips_detail ?? []).reduce((s, v) => s + Number(v), 0);
    rows.set(key, row);
  }

  // ── Kitchen event tip-outs (Toast side), folded into the kitchen payout ──
  for (const kt of kitchenTipouts) {
    const emp = kt.employee_id ? byId.get(kt.employee_id) : byName.get(kt.employee_name.trim().toLowerCase());
    const employeeId = emp?.id ?? kt.employee_id ?? "";
    const existing = employeeId ? rows.get(squareKey(employeeId)) : undefined;
    if (existing) {
      existing.eventTips += Number(kt.amount);
      continue;
    }
    const key = employeeId ? `${employeeId}|kitchen` : `name:${kt.employee_name}|kitchen`;
    const roles = payoutRoles(emp);
    const row =
      rows.get(key) ??
      blank(key, employeeId, emp ? employeeDisplayName(emp) : kt.employee_name, roles.length > 0 ? roles : ["kitchen"], "kitchen");
    row.eventTips += Number(kt.amount);
    rows.set(key, row);
  }

  // ── Axe coach history from before coach entry moved into the Square sheet ──
  const addCoachLegacy = (employeeId: string, tipsDelta: number, eventDelta: number) => {
    const key = rows.has(squareKey(employeeId)) ? squareKey(employeeId) : `${employeeId}|axe_coach`;
    const roles = payoutRoles(byId.get(employeeId));
    const row =
      rows.get(key) ??
      blank(key, employeeId, nameFor(employeeId), roles.length > 0 ? roles : ["axe_coach"], "axe_coach");
    row.tips += tipsDelta;
    row.eventTips += eventDelta;
    rows.set(key, row);
  };
  for (const t of coachTips) addCoachLegacy(t.employee_id, Number(t.calculated_tips) || 0, 0);
  for (const a of coachAssignments) addCoachLegacy(a.employee_id, 0, Number(a.split_amount));

  return Array.from(rows.values())
    .map((row) => ({
      ...row,
      hours: round2(row.hours),
      tips: round2(row.tips),
      extraTips: round2(row.extraTips),
      eventTips: round2(row.eventTips),
      total: round2(row.primaryRole === "bartender" ? row.total : row.tips + row.extraTips + row.eventTips),
    }))
    .filter((row) => row.hours > 0 || row.total > 0)
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

export interface ReportTotals {
  hours: number;
  tips: number;
  extraTips: number;
  eventTips: number;
  total: number;
}

export function sumReportRows(rows: ReportRow[]): ReportTotals {
  return rows.reduce<ReportTotals>(
    (acc, r) => ({
      hours: round2(acc.hours + r.hours),
      tips: round2(acc.tips + r.tips),
      extraTips: round2(acc.extraTips + r.extraTips),
      eventTips: round2(acc.eventTips + r.eventTips),
      total: round2(acc.total + r.total),
    }),
    { hours: 0, tips: 0, extraTips: 0, eventTips: 0, total: 0 },
  );
}
